// Contributor badges. Every badge is just "reach <goal> of <stat>", and the stats only count work an admin
// approved, so badges can't be farmed with pending or rejected submissions.
//
// Shared by the API (functions/_shared/points.js, functions/api/profile) and the UI, so the definitions live in one place.
//
// Stats, per contributor:
//   ships     approved new wrecks they submitted
//   edits     approved edits they suggested
//   complete  approved wrecks of theirs that have a photo and a real description
//   photos    photo contributions: approved wrecks submitted with photos + approved edits that set photos
//   reports   problem reports of theirs that an admin resolved (acted on)
//   points    total leaderboard points

export const BADGES = [
  { id: "first_wreck", icon: "⚓", name: "First Wreck", stat: "ships", goal: 1, description: "Had your first new wreck approved." },
  { id: "wreck_hunter", icon: "🧭", name: "Wreck Hunter", stat: "ships", goal: 5, description: "Had 5 new wrecks approved." },
  { id: "cartographer", icon: "🗺️", name: "Cartographer", stat: "ships", goal: 25, description: "Had 25 new wrecks approved." },
  { id: "sharp_eye", icon: "🔍", name: "Sharp Eye", stat: "edits", goal: 1, description: "Had your first edit approved." },
  { id: "editor", icon: "✏️", name: "Editor", stat: "edits", goal: 10, description: "Had 10 edits approved." },
  { id: "veteran_editor", icon: "📚", name: "Veteran Editor", stat: "edits", goal: 50, description: "Had 50 edits approved." },
  { id: "photographer", icon: "📷", name: "Photographer", stat: "photos", goal: 1, description: "Had a photo approved on the map (on a new wreck or in an edit)." },
  { id: "gallery_curator", icon: "🖼️", name: "Gallery Curator", stat: "photos", goal: 10, description: "Had 10 approved contributions with photos." },
  { id: "completionist", icon: "✅", name: "Completionist", stat: "complete", goal: 3, description: "Had 3 approved wrecks with both a photo and a full description." },
  { id: "eagle_eye", icon: "🦅", name: "Eagle Eye", stat: "reports", goal: 3, description: "Reported 3 problems that admins acted on." },
  { id: "century", icon: "💯", name: "Century Club", stat: "points", goal: 100, description: "Reached 100 points." },
];

export const BADGES_BY_ID = Object.fromEntries(BADGES.map((b) => [b.id, b]));

const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

// Every badge with the person's progress: [{ ...badge, value, earned }].
export function badgeProgress(stats = {}) {
  return BADGES.map((b) => {
    const value = num(stats[b.stat]);
    return { ...b, value, earned: value >= b.goal };
  });
}

// Just the ids of the badges they have, in definition order.
export const earnedBadgeIds = (stats) => badgeProgress(stats).filter((b) => b.earned).map((b) => b.id);

// From a badgeProgress() list: the unearned badge that's closest to done (highest fraction), or null when all are earned.
export function closestUnearned(progress) {
  const open = (progress || []).filter((b) => !b.earned);
  if (!open.length) return null;
  return open.reduce((best, b) => (b.value / b.goal > best.value / best.goal ? b : best));
}

// The badge they're closest to earning next, from raw stats.
export const nextBadge = (stats) => closestUnearned(badgeProgress(stats));
